import React, { useState, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useLMS } from '../../context/LMSContext';
import { db } from '../../lib/supabase';
import { jsPDF } from 'jspdf';

/**
 * ReflectionManager – Admin/Professor side for weekly reflections.
 *
 * Handles the whole lifecycle in one place: write the questions for a week,
 * make sure the week actually shows a Reflection block to students, read what
 * came back, and export it as PDF (per student or the whole cohort).
 *
 * Questions live on the module (week), so they are shared by every cohort.
 * Responses are per student, so the exports below are always cohort-scoped.
 */
const ReflectionManager = ({ cohortId }) => {
  const { modules, getUsersForCohort, getCohortById, currentUser, addModuleItem, deleteModuleItem } = useLMS();

  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [responsesWeek, setResponsesWeek] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);

  // Question editing
  const [draftQuestion, setDraftQuestion] = useState({});   // moduleId -> text
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [busy, setBusy] = useState(false);
  const [expandedEditor, setExpandedEditor] = useState({}); // moduleId -> bool

  const students = getUsersForCohort(cohortId).filter(s => s.role === 'student');
  const cohort = getCohortById(cohortId);

  const sortedModules = useMemo(() => {
    return [...modules]
      .filter(m => m.weekNumber || m.week_number)
      .sort((a, b) => (a.weekNumber || a.week_number) - (b.weekNumber || b.week_number));
  }, [modules]);

  useEffect(() => {
    if (cohortId) loadAll();
  }, [cohortId]);

  const loadAll = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [qRes, rRes] = await Promise.all([
        db.getAllReflectionQuestions(),
        db.getCohortReflectionResponses(cohortId)
      ]);

      // Surface failures instead of rendering an empty state that looks like
      // "no reflections exist" when it is really a permissions or query error.
      if (qRes.error) throw qRes.error;
      if (rRes.error) throw rRes.error;

      setQuestions(qRes.data || []);
      setResponses(rRes.data || []);
    } catch (err) {
      console.error('Error loading reflections:', err);
      Sentry.captureException(err);
      setLoadError(err.message || String(err));
    }
    setLoading(false);
  };

  // Group questions by module
  const questionsByModule = useMemo(() => {
    const map = {};
    questions.forEach(q => {
      if (!map[q.module_id]) map[q.module_id] = [];
      map[q.module_id].push(q);
    });
    Object.keys(map).forEach(k => {
      map[k].sort((a, b) => a.question_order - b.question_order);
    });
    return map;
  }, [questions]);

  // Build response lookup: questionId -> studentId -> response
  const responseLookup = useMemo(() => {
    const map = {};
    responses.forEach(r => {
      if (!map[r.question_id]) map[r.question_id] = {};
      map[r.question_id][r.student_id] = r;
    });
    return map;
  }, [responses]);

  const openResponses = (weekNum) => {
    setResponsesWeek(weekNum);
    setShowResponsesModal(true);
  };

  // Sort students alphabetically by first name
  const sortedStudents = useMemo(() => {
    return [...students].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [students]);

  // =========================================================================
  // QUESTION EDITING
  //
  // Questions used to be write-once: they could only be typed into the Add
  // Item modal in Master Modules and never changed afterwards. Everything
  // needed to manage them now lives here.
  // =========================================================================

  const hasReflectionItem = (module) =>
    (module.items || []).some(i => i.type === 'reflection');

  // Students only see a reflection block on a week that carries a reflection
  // module item, so creating the first question creates that item too.
  const ensureReflectionItem = async (module) => {
    if (hasReflectionItem(module)) return null;

    const result = await addModuleItem(module.id, {
      type: 'reflection',
      title: 'Weekly Reflection',
      description: 'Answer this week\'s reflection questions.',
      dueDay: '',
      points: 0
    });

    return result?.error || null;
  };

  // The inverse of ensureReflectionItem: once the last question for a week is
  // gone, the module item it created has nothing to show, and students would
  // otherwise see an empty "Weekly Reflection" block on that week.
  //
  // Reflection answers live in reflection_responses, not assignment_submissions,
  // so a reflection item should never have submissions attached. If one somehow
  // does, the item has been used for something else and is not ours to delete.
  const removeReflectionItemIfUnused = async (module) => {
    const item = (module?.items || []).find(i => i.type === 'reflection');
    if (!item) return { removed: false, error: null, keptForSubmissions: false };

    const { count, error: countError } = await db.countItemSubmissions(item.id);
    if (countError) {
      return { removed: false, error: countError.message || String(countError), keptForSubmissions: false };
    }
    if (count > 0) {
      return { removed: false, error: null, keptForSubmissions: true };
    }

    const result = await deleteModuleItem(item.id);
    return {
      removed: !result?.error,
      error: result?.error || null,
      keptForSubmissions: false
    };
  };

  const handleAddQuestion = async (module) => {
    const text = (draftQuestion[module.id] || '').trim();
    if (!text) return;

    setBusy(true);
    try {
      const existing = questionsByModule[module.id] || [];
      const nextOrder = existing.length > 0
        ? Math.max(...existing.map(q => q.question_order || 0)) + 1
        : 1;

      const { error } = await db.createReflectionQuestion({
        moduleId: module.id,
        questionText: text,
        questionOrder: nextOrder,
        createdBy: currentUser?.id
      });
      if (error) throw error;

      const itemError = await ensureReflectionItem(module);
      if (itemError) {
        alert(`Question saved, but the week could not be published to students: ${itemError}`);
      }

      setDraftQuestion(prev => ({ ...prev, [module.id]: '' }));
      await loadAll();
    } catch (err) {
      console.error('Error adding reflection question:', err);
      Sentry.captureException(err);
      alert(`Failed to save the question: ${err.message || err}`);
    }
    setBusy(false);
  };

  const handleSaveEdit = async (question) => {
    const text = editingText.trim();
    if (!text) return;

    setBusy(true);
    try {
      const { error } = await db.updateReflectionQuestion(question.id, {
        questionText: text,
        questionOrder: question.question_order
      });
      if (error) throw error;

      setEditingId(null);
      setEditingText('');
      await loadAll();
    } catch (err) {
      console.error('Error updating reflection question:', err);
      Sentry.captureException(err);
      alert(`Failed to update the question: ${err.message || err}`);
    }
    setBusy(false);
  };

  const handleDeleteQuestion = async (question) => {
    const answerCount = Object.values(responseLookup[question.id] || {})
      .filter(r => r.response_text).length;

    const module = modules.find(m => m.id === question.module_id);
    const isLastQuestion = (questionsByModule[question.module_id] || [])
      .filter(q => q.id !== question.id).length === 0;

    const parts = [
      answerCount > 0
        ? `This question has ${answerCount} student response${answerCount === 1 ? '' : 's'}. Deleting the question also deletes those responses permanently.`
        : 'Delete this reflection question?'
    ];
    if (isLastQuestion) {
      parts.push('It is the last question for this week, so the Weekly Reflection block will be removed from the week as well.');
    }
    if (answerCount > 0) parts.push('Continue?');

    if (!window.confirm(parts.join('\n\n'))) return;

    setBusy(true);
    try {
      const { error } = await db.deleteReflectionQuestion(question.id);
      if (error) throw error;

      // Take the now-empty module item with it, so students are not left
      // looking at a Weekly Reflection block with nothing inside.
      if (isLastQuestion && module) {
        const cleanup = await removeReflectionItemIfUnused(module);
        if (cleanup.error) {
          alert(`Question deleted, but the empty Weekly Reflection block could not be removed from the week: ${cleanup.error}`);
        } else if (cleanup.keptForSubmissions) {
          alert('Question deleted. The Weekly Reflection block was left in place because student submissions are attached to it.');
        }
      }

      await loadAll();
    } catch (err) {
      console.error('Error deleting reflection question:', err);
      Sentry.captureException(err);
      alert(`Failed to delete the question: ${err.message || err}`);
    }
    setBusy(false);
  };

  // Swap question_order with the neighbour in the given direction.
  const handleMoveQuestion = async (module, index, direction) => {
    const list = questionsByModule[module.id] || [];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;

    const a = list[index];
    const b = list[target];

    setBusy(true);
    try {
      const [resA, resB] = await Promise.all([
        db.updateReflectionQuestion(a.id, {
          questionText: a.question_text,
          questionOrder: b.question_order
        }),
        db.updateReflectionQuestion(b.id, {
          questionText: b.question_text,
          questionOrder: a.question_order
        })
      ]);
      if (resA.error) throw resA.error;
      if (resB.error) throw resB.error;
      await loadAll();
    } catch (err) {
      console.error('Error reordering reflection questions:', err);
      Sentry.captureException(err);
      alert(`Failed to reorder: ${err.message || err}`);
    }
    setBusy(false);
  };

  const handlePublishWeek = async (module) => {
    setBusy(true);
    const itemError = await ensureReflectionItem(module);
    setBusy(false);
    if (itemError) {
      alert(`Could not publish to students: ${itemError}`);
    }
  };

  // =========================================================================
  // PDF GENERATION
  // =========================================================================

  const generateStudentPDF = (student) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const addPageIfNeeded = (requiredSpace) => {
      if (y + requiredSpace > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Weekly Reflections', margin, y);
    y += 8;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(student.name || 'Student', margin, y);
    y += 5;
    if (cohort) {
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(cohort.name, margin, y);
      doc.setTextColor(0);
      y += 5;
    }
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, margin, y);
    doc.setTextColor(0);
    y += 10;

    doc.setDrawColor(200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    sortedModules.forEach(module => {
      const weekNum = module.weekNumber || module.week_number;
      const moduleQuestions = questionsByModule[module.id] || [];
      if (moduleQuestions.length === 0) return;

      addPageIfNeeded(20);

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Week ${weekNum}: ${module.title}`, margin, y);
      y += 8;

      moduleQuestions.forEach((q, qi) => {
        addPageIfNeeded(15);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        const qLines = doc.splitTextToSize(`${qi + 1}. ${q.question_text}`, contentWidth);
        doc.text(qLines, margin, y);
        y += qLines.length * 5 + 2;

        const response = responseLookup[q.id]?.[student.id];
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        if (response && response.response_text) {
          const rLines = doc.splitTextToSize(response.response_text, contentWidth - 5);
          doc.setTextColor(50);
          rLines.forEach(line => {
            addPageIfNeeded(6);
            doc.text(line, margin + 3, y);
            y += 5;
          });
          doc.setTextColor(0);
        } else {
          doc.setTextColor(150);
          doc.text('No response submitted', margin + 3, y);
          doc.setTextColor(0);
          y += 5;
        }
        y += 5;
      });

      y += 5;
    });

    doc.save(`Reflections_${(student.name || 'Student').replace(/\s+/g, '_')}.pdf`);
  };

  const generateCohortPDF = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const addPageIfNeeded = (requiredSpace) => {
      if (y + requiredSpace > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    // Cover page
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Weekly Reflections', margin, y);
    y += 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    if (cohort) doc.text(cohort.name, margin, y);
    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, margin, y);
    doc.text(`${sortedStudents.length} students`, margin, y + 5);
    doc.setTextColor(0);

    sortedStudents.forEach(student => {
      doc.addPage();
      y = margin;

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(student.name || 'Student', margin, y);
      y += 7;
      doc.setDrawColor(200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 8;

      sortedModules.forEach(module => {
        const weekNum = module.weekNumber || module.week_number;
        const moduleQuestions = questionsByModule[module.id] || [];
        if (moduleQuestions.length === 0) return;

        addPageIfNeeded(18);

        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text(`Week ${weekNum}: ${module.title}`, margin, y);
        y += 7;

        moduleQuestions.forEach((q, qi) => {
          addPageIfNeeded(14);

          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          const qLines = doc.splitTextToSize(`${qi + 1}. ${q.question_text}`, contentWidth);
          doc.text(qLines, margin, y);
          y += qLines.length * 4.5 + 2;

          const response = responseLookup[q.id]?.[student.id];
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          if (response && response.response_text) {
            const rLines = doc.splitTextToSize(response.response_text, contentWidth - 5);
            doc.setTextColor(50);
            rLines.forEach(line => {
              addPageIfNeeded(5);
              doc.text(line, margin + 3, y);
              y += 4.5;
            });
            doc.setTextColor(0);
          } else {
            doc.setTextColor(150);
            doc.text('No response submitted', margin + 3, y);
            doc.setTextColor(0);
            y += 4.5;
          }
          y += 4;
        });

        y += 4;
      });
    });

    doc.save(`Reflections_${(cohort?.name || 'Cohort').replace(/\s+/g, '_')}_All.pdf`);
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
        <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
        <p>Loading reflections...</p>
      </div>
    );
  }

  const hasAnyQuestions = questions.length > 0;

  return (
    <Sentry.ErrorBoundary fallback={<div style={{ padding: '20px', color: '#dc2626' }}>Error loading reflection manager.</div>}>
      <div className="reflection-manager">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Student Reflections</h3>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '13px' }}>
              Write the questions for each week, then read and download what students wrote back.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="btn-secondary" onClick={loadAll} style={{ fontSize: '13px' }}>Refresh</button>
            {hasAnyQuestions && (
              <>
                <button className="btn-secondary" onClick={generateCohortPDF} style={{ fontSize: '13px' }}>
                  Download All PDF
                </button>
                <button className="btn-secondary" onClick={() => setShowPrintView(true)} style={{ fontSize: '13px' }}>
                  Printable View
                </button>
              </>
            )}
          </div>
        </div>

        {loadError && (
          <div style={{
            padding: '12px 16px', marginBottom: '16px', borderRadius: '8px',
            backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '13px'
          }}>
            <strong>Could not load reflections.</strong> {loadError}
          </div>
        )}

        {!hasAnyQuestions && !loadError && (
          <div style={{
            padding: '16px', marginBottom: '16px', borderRadius: '8px',
            backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: '13px'
          }}>
            No reflection questions have been written yet. Add one to any week below and it becomes
            visible to students in that week straight away.
          </div>
        )}

        {/* Weeks: question editor + response summary */}
        {sortedModules.map(module => {
          const weekNum = module.weekNumber || module.week_number;
          const moduleQuestions = questionsByModule[module.id] || [];
          // Undefined means "not toggled yet": weeks that already have questions
          // start open, empty weeks start collapsed. An explicit toggle wins.
          const isOpen = expandedEditor[module.id] ?? moduleQuestions.length > 0;

          const responseCount = moduleQuestions.reduce((acc, q) => {
            const qResponses = Object.values(responseLookup[q.id] || {});
            return acc + qResponses.filter(r => r.response_text).length;
          }, 0);
          const totalPossible = moduleQuestions.length * students.length;
          const published = hasReflectionItem(module);

          return (
            <div key={module.id} style={{
              marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden'
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 16px', backgroundColor: '#f9fafb', flexWrap: 'wrap', gap: '8px'
              }}>
                <div>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>
                    Week {weekNum}: {module.title}
                  </span>
                  <span style={{ marginLeft: '12px', fontSize: '12px', color: '#6b7280' }}>
                    {moduleQuestions.length} question{moduleQuestions.length !== 1 ? 's' : ''}
                    {totalPossible > 0 && ` · ${responseCount}/${totalPossible} responses`}
                  </span>
                  {moduleQuestions.length > 0 && !published && (
                    <span style={{
                      marginLeft: '10px', fontSize: '11px', fontWeight: '600', padding: '2px 8px',
                      borderRadius: '10px', backgroundColor: '#fef3c7', color: '#92400e'
                    }}>
                      Not visible to students
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {moduleQuestions.length > 0 && !published && (
                    <button
                      className="btn-secondary"
                      onClick={() => handlePublishWeek(module)}
                      disabled={busy}
                      style={{ fontSize: '12px', padding: '4px 10px' }}
                    >
                      Show to students
                    </button>
                  )}
                  {moduleQuestions.length > 0 && (
                    <button
                      className="btn-secondary"
                      onClick={() => openResponses(weekNum)}
                      style={{ fontSize: '12px', padding: '4px 10px' }}
                    >
                      View Responses
                    </button>
                  )}
                  <button
                    className="btn-secondary"
                    onClick={() => setExpandedEditor(prev => ({ ...prev, [module.id]: !isOpen }))}
                    style={{ fontSize: '12px', padding: '4px 10px' }}
                  >
                    {isOpen ? 'Hide questions' : 'Add questions'}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div style={{ padding: '12px 16px' }}>
                  {moduleQuestions.map((q, i) => (
                    <div key={q.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '8px',
                      padding: '8px 0', borderBottom: i < moduleQuestions.length - 1 ? '1px solid #f3f4f6' : 'none'
                    }}>
                      <span style={{ fontSize: '13px', color: '#9ca3af', paddingTop: '4px' }}>{i + 1}.</span>

                      {editingId === q.id ? (
                        <>
                          <textarea
                            rows={2}
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            style={{
                              flex: 1, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px',
                              fontSize: '13px', fontFamily: 'inherit', resize: 'vertical'
                            }}
                          />
                          <button
                            className="btn-primary"
                            onClick={() => handleSaveEdit(q)}
                            disabled={busy || !editingText.trim()}
                            style={{ fontSize: '12px', padding: '4px 10px' }}
                          >
                            Save
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={() => { setEditingId(null); setEditingText(''); }}
                            style={{ fontSize: '12px', padding: '4px 10px' }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <span style={{ flex: 1, fontWeight: '500', fontSize: '13px', color: '#374151', paddingTop: '4px' }}>
                            {q.question_text}
                          </span>
                          <button
                            className="btn-icon"
                            title="Move up"
                            disabled={busy || i === 0}
                            onClick={() => handleMoveQuestion(module, i, -1)}
                            style={{ fontSize: '12px' }}
                          >
                            ↑
                          </button>
                          <button
                            className="btn-icon"
                            title="Move down"
                            disabled={busy || i === moduleQuestions.length - 1}
                            onClick={() => handleMoveQuestion(module, i, 1)}
                            style={{ fontSize: '12px' }}
                          >
                            ↓
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={() => { setEditingId(q.id); setEditingText(q.question_text); }}
                            style={{ fontSize: '12px', padding: '4px 10px' }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-icon"
                            title="Delete question"
                            disabled={busy}
                            onClick={() => handleDeleteQuestion(q)}
                            style={{ fontSize: '12px', color: '#dc2626' }}
                          >
                            🗑
                          </button>
                        </>
                      )}
                    </div>
                  ))}

                  <div style={{ display: 'flex', gap: '8px', marginTop: moduleQuestions.length > 0 ? '12px' : '0' }}>
                    <textarea
                      rows={2}
                      value={draftQuestion[module.id] || ''}
                      onChange={(e) => setDraftQuestion(prev => ({ ...prev, [module.id]: e.target.value }))}
                      placeholder={`New reflection question for Week ${weekNum}...`}
                      style={{
                        flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px',
                        fontSize: '13px', fontFamily: 'inherit', resize: 'vertical'
                      }}
                    />
                    <button
                      className="btn-primary"
                      onClick={() => handleAddQuestion(module)}
                      disabled={busy || !(draftQuestion[module.id] || '').trim()}
                      style={{ fontSize: '13px', padding: '6px 14px', alignSelf: 'flex-start' }}
                    >
                      {busy ? 'Saving...' : 'Add'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Per-student downloads */}
        {hasAnyQuestions && sortedStudents.length > 0 && (
          <div style={{ marginTop: '24px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb' }}>
              <span style={{ fontWeight: '600', fontSize: '14px' }}>Download by student</span>
              <span style={{ marginLeft: '10px', fontSize: '12px', color: '#6b7280' }}>
                One PDF per student, every week they have answered
              </span>
            </div>
            <div style={{ padding: '4px 16px 12px' }}>
              {sortedStudents.map(student => {
                const answered = questions.filter(
                  q => responseLookup[q.id]?.[student.id]?.response_text
                ).length;
                return (
                  <div key={student.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 0', borderBottom: '1px solid #f3f4f6'
                  }}>
                    <span style={{ fontSize: '13px' }}>
                      {student.name}
                      <span style={{ marginLeft: '8px', color: '#9ca3af', fontSize: '12px' }}>
                        {answered}/{questions.length} answered
                      </span>
                    </span>
                    <button
                      className="btn-secondary"
                      onClick={() => generateStudentPDF(student)}
                      style={{ fontSize: '12px', padding: '4px 10px' }}
                    >
                      Download PDF
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* View Responses Modal */}
        {showResponsesModal && responsesWeek && (
          <div className="modal-overlay" onClick={() => setShowResponsesModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '80vh' }}>
              <div className="modal-header">
                <h3>Week {responsesWeek} Responses</h3>
                <button className="modal-close" onClick={() => setShowResponsesModal(false)}>&times;</button>
              </div>
              <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                {(() => {
                  const module = sortedModules.find(m => (m.weekNumber || m.week_number) === responsesWeek);
                  if (!module) return <p>Module not found.</p>;
                  const weekQuestions = questionsByModule[module.id] || [];
                  if (weekQuestions.length === 0) return <p>No questions for this week.</p>;

                  return sortedStudents.map(student => (
                    <div key={student.id} style={{ marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '14px' }}>{student.name}</strong>
                        <button
                          className="btn-secondary"
                          onClick={() => generateStudentPDF(student)}
                          style={{ fontSize: '11px', padding: '2px 8px' }}
                        >
                          Download PDF
                        </button>
                      </div>
                      {weekQuestions.map((q, qi) => {
                        const resp = responseLookup[q.id]?.[student.id];
                        return (
                          <div key={q.id} style={{ marginBottom: '8px', paddingLeft: '8px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '2px' }}>
                              {qi + 1}. {q.question_text}
                            </div>
                            <div style={{ fontSize: '13px', color: resp?.response_text ? '#1f2937' : '#9ca3af', paddingLeft: '4px', whiteSpace: 'pre-wrap' }}>
                              {resp?.response_text || 'No response'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Printable View Modal */}
        {showPrintView && (
          <div className="modal-overlay" onClick={() => setShowPrintView(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
              <div className="modal-header" style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                <h3>All Reflections - Printable View</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn-secondary" onClick={() => window.print()} style={{ fontSize: '12px' }}>
                    Print
                  </button>
                  <button className="modal-close" onClick={() => setShowPrintView(false)}>&times;</button>
                </div>
              </div>
              <div className="modal-body" id="printable-reflections" style={{ maxHeight: '80vh', overflowY: 'auto', padding: '20px 24px' }}>
                <h2 style={{ textAlign: 'center', marginBottom: '4px' }}>Weekly Reflections</h2>
                {cohort && <p style={{ textAlign: 'center', color: '#6b7280', marginTop: 0 }}>{cohort.name}</p>}

                {sortedStudents.map(student => (
                  <div key={student.id} style={{ marginBottom: '32px', pageBreakBefore: 'always' }}>
                    <h3 style={{ borderBottom: '2px solid #e5e7eb', paddingBottom: '6px', marginBottom: '12px' }}>
                      {student.name}
                    </h3>

                    {sortedModules.map(module => {
                      const weekNum = module.weekNumber || module.week_number;
                      const moduleQuestions = questionsByModule[module.id] || [];
                      if (moduleQuestions.length === 0) return null;

                      return (
                        <div key={module.id} style={{ marginBottom: '16px', paddingLeft: '8px' }}>
                          <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: '#374151' }}>
                            Week {weekNum}: {module.title}
                          </h4>
                          {moduleQuestions.map((q, qi) => {
                            const resp = responseLookup[q.id]?.[student.id];
                            return (
                              <div key={q.id} style={{ marginBottom: '10px', paddingLeft: '8px' }}>
                                <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>
                                  {qi + 1}. {q.question_text}
                                </div>
                                <div style={{ fontSize: '13px', color: resp?.response_text ? '#1f2937' : '#9ca3af', paddingLeft: '4px', whiteSpace: 'pre-wrap' }}>
                                  {resp?.response_text || 'No response'}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Sentry.ErrorBoundary>
  );
};

export default ReflectionManager;
